import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Form, Input, Modal, Select, Skeleton, Typography, notification } from 'antd';
import { BankOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';

/**
 * Facility account details (finance only): account number, bank and account
 * name, used on the CFO Payment advice. Anyone who can open the facility or
 * the Pending CFO list sees them; only holders of facility_accounts.manage
 * get the edit controls (the API re-checks and returns can_edit).
 *
 * Two entry points, one facility record:
 *   <FacilityAccountDetailsSection>  — facility page, its own section
 *   <FacilityAccountDetailsModal>    — Pending CFO facility list button
 */
export const FACILITY_ACCOUNTS_PERMISSION = 'facility_accounts.manage';

export function useCanManageFacilityAccounts() {
  const { hasPermission } = usePermissions();
  return hasPermission(FACILITY_ACCOUNTS_PERMISSION);
}

let banksCache = null;
async function loadBanks() {
  if (!banksCache) {
    banksCache = lib.getBanksV2().catch((e) => {
      banksCache = null;
      throw e;
    });
  }
  return banksCache;
}

function useAccountDetails(facilityId, enabled = true) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!facilityId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      setDetails(await lib.getFacilityAccountDetailsV2(facilityId));
    } catch (e) {
      setError(e?.message || 'Could not load account details');
    } finally {
      setLoading(false);
    }
  }, [facilityId, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { details, setDetails, loading, error, reload };
}

function ReadOnlyDetails({ details }) {
  if (!details?.has_details) {
    return <Typography.Text type="secondary">No account details yet.</Typography.Text>;
  }
  return (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Account number">
        <Typography.Text copyable style={{ fontFamily: 'monospace' }}>{details.account_number}</Typography.Text>
      </Descriptions.Item>
      <Descriptions.Item label="Bank name">{details.bank_name}</Descriptions.Item>
      <Descriptions.Item label="Account name">{details.account_name}</Descriptions.Item>
      {details.updated_at ? (
        <Descriptions.Item label="Last changed">
          {dayjs(details.updated_at).format('D MMM YYYY, h:mm A')}
          {details.updated_by_name ? ` by ${details.updated_by_name}` : ''}
        </Descriptions.Item>
      ) : null}
    </Descriptions>
  );
}

function AccountDetailsForm({ facilityName, details, source, onSaved, onCancel }) {
  const [form] = Form.useForm();
  const [banks, setBanks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBanks()
      .then(setBanks)
      .catch((e) => notification.error({ message: 'Could not load the bank list', description: e?.message }));
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      account_number: details?.account_number || '',
      bank_id: details?.bank_id || undefined,
      account_name: details?.account_name || '',
    });
  }, [details, form]);

  const submit = async (values) => {
    setSaving(true);
    try {
      const saved = await lib.saveFacilityAccountDetailsV2(details.facility_id, { ...values, source });
      notification.success({ message: 'Account details saved' });
      onSaved?.(saved);
    } catch (e) {
      const fieldErrors = Object.entries(e?.errors || {})
        .filter(([name]) => ['account_number', 'bank_id', 'account_name'].includes(name))
        .map(([name, msg]) => ({ name, errors: [msg] }));
      if (fieldErrors.length) {
        form.setFields(fieldErrors);
      } else {
        notification.error({ message: 'Could not save account details', description: e?.message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={submit} requiredMark>
      <Form.Item
        label="Account number"
        name="account_number"
        normalize={(v) => String(v || '').replace(/\D/g, '').slice(0, 10)}
        rules={[
          { required: true, message: 'Account number is required' },
          { pattern: /^\d{10}$/, message: 'Nigerian account numbers are 10 digits' },
        ]}
      >
        <Input inputMode="numeric" maxLength={10} placeholder="10 digits" style={{ fontFamily: 'monospace' }} />
      </Form.Item>
      <Form.Item label="Bank name" name="bank_id" rules={[{ required: true, message: 'Select a bank' }]}>
        <Select
          showSearch
          placeholder="Select a bank"
          optionFilterProp="label"
          loading={!banks.length}
          options={banks.map((b) => ({ value: b.bank_id, label: b.bank_name }))}
        />
      </Form.Item>
      <Form.Item
        label="Account name"
        name="account_name"
        extra={facilityName ? `Should match the facility: ${facilityName}` : undefined}
        rules={[
          { required: true, whitespace: true, message: 'Account name is required' },
          { max: 150, message: 'At most 150 characters' },
        ]}
      >
        <Input maxLength={150} />
      </Form.Item>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
        <Button type="primary" htmlType="submit" loading={saving}>
          Save account details
        </Button>
      </div>
    </Form>
  );
}

/** Facility page section: read-only for facility editors, editable with the permission. */
export function FacilityAccountDetailsSection({ facilityId, facilityName }) {
  const { details, setDetails, loading, error } = useAccountDetails(facilityId);
  const [editing, setEditing] = useState(false);
  const canEdit = Boolean(details?.can_edit);

  return (
    <section className="facility-account-details" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          <BankOutlined /> Account details
        </Typography.Title>
        {canEdit && !editing && details ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>
            {details.has_details ? 'Edit' : 'Add account details'}
          </Button>
        ) : null}
      </div>
      {loading && !details ? <Skeleton active paragraph={{ rows: 2 }} /> : null}
      {error ? <Alert type="warning" showIcon message={error} /> : null}
      {details && !editing ? <ReadOnlyDetails details={details} /> : null}
      {details && editing ? (
        <AccountDetailsForm
          facilityName={facilityName || details.facility_name}
          details={details}
          source="facility_page"
          onSaved={(saved) => {
            setDetails(saved);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}
      {details && !canEdit ? (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
          Only finance (Manage facility account details) can change these.
        </Typography.Paragraph>
      ) : null}
    </section>
  );
}

/**
 * Pending CFO facility list: "Add account details" opens straight in edit,
 * "View account details" opens read-only with an Edit option.
 */
export function FacilityAccountDetailsModal({ open, facilityId, facilityName, startEditing = false, onClose, onSaved }) {
  const { details, setDetails, loading, error } = useAccountDetails(facilityId, open);
  const [editing, setEditing] = useState(startEditing);
  const canEdit = Boolean(details?.can_edit);

  useEffect(() => {
    if (open) setEditing(startEditing);
  }, [open, startEditing]);

  return (
    <Modal
      open={open}
      title={`Account details — ${facilityName || details?.facility_name || 'Facility'}`}
      onCancel={onClose}
      footer={
        editing ? null : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {canEdit && details ? (
              <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                {details.has_details ? 'Edit' : 'Add account details'}
              </Button>
            ) : null}
            <Button type="primary" onClick={onClose}>Close</Button>
          </div>
        )
      }
      destroyOnClose
    >
      {loading && !details ? <Skeleton active paragraph={{ rows: 3 }} /> : null}
      {error ? <Alert type="warning" showIcon message={error} /> : null}
      {details && (!editing || !canEdit) ? <ReadOnlyDetails details={details} /> : null}
      {details && editing && canEdit ? (
        <AccountDetailsForm
          facilityName={facilityName || details.facility_name}
          details={details}
          source="pending_cfo"
          onSaved={(saved) => {
            setDetails(saved);
            setEditing(false);
            onSaved?.(saved);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}
    </Modal>
  );
}
